import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer, IconButton, Tab, Tabs } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, memo, type SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { INK, INK_BLACK, SURFACE } from "../../theme/tokens";
import type { StaffingIssue } from "../../utils/autoStaffing";
import { IssuePriority, isSummaryTabHiddenForDepartment } from "../../utils/models";
import DrawerTabsContent from "../DrawerTabsContent/DrawerTabsContent";
import { TabValue } from "../DrawerTabsContent/types";
import { DRAWER_WIDTH_IN_PX } from "./utils";

const DrawerHeader = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(0, 1),
	overflow: "visible",
	...theme.mixins.toolbar,
	justifyContent: "flex-start",
}));

type RecommendationsDrawerProps = {
	open: boolean;
	handleOpenDialog: () => void;
	isAutoScheduler: boolean;
	recommendations: StaffingIssue[];
	handleDrawerClose: () => void;
	setChosenLevel: (value: IssuePriority) => void;
	implementedChanges: Record<string, boolean>;
	setImplementedChanges: (value: Record<string, boolean>) => void;
	weekIndex: number;
	startingDate: DateTime;
	handleRemoveRecommendation: (index: number) => void;
	date?: DateTime;
	requestId?: string | null;
};

const RecommendationsDrawer: FC<RecommendationsDrawerProps> = ({
	open,
	handleOpenDialog,
	isAutoScheduler,
	recommendations,
	handleDrawerClose,
	setChosenLevel,
	implementedChanges,
	setImplementedChanges,
	weekIndex,
	startingDate,
	handleRemoveRecommendation,
	date,
	requestId,
}) => {
	const { isAnyDemo, isMedicalDepartment, chosenDepartment } = useHospitalData();
	const { track } = useAnalytics();
	const isTimeOffRequestsEnabled = useFeatureFlag(FEATURE_FLAGS.TimeOffRequestsEnabled);

	const [currentTab, setCurrentTab] = useState<TabValue>(isMedicalDepartment ? TabValue.Chat : TabValue.Requests);
	const [selectedPriority, setSelectedPriority] = useState<IssuePriority | "all">("all");
	const topGap = "64px";

	useEffect(() => {
		if (requestId && open) {
			setCurrentTab(isMedicalDepartment ? TabValue.MedicalDepartmentRequests : TabValue.Requests);
		}
	}, [requestId, open, isMedicalDepartment]);

	const onRemoveRecommendation = useCallback(
		(recIdx: number, _: number, __: boolean) => {
			handleRemoveRecommendation(recIdx);
		},
		[handleRemoveRecommendation],
	);

	const commonRecommendationProps = useMemo(
		() => ({
			implementedChanges,
			setImplementedChanges,
			weekIndex,
			startingDate,
			onRemoveRecommendation: onRemoveRecommendation,
			isRequestedTab: false,
			isAutoScheduler,
		}),
		[implementedChanges, weekIndex, startingDate, isAutoScheduler, onRemoveRecommendation, setImplementedChanges],
	);

	const recommendationsByPriority = useMemo<Record<IssuePriority | "all", StaffingIssue[]>>(
		() => ({
			all: recommendations,
			[IssuePriority.TopPriority]: recommendations.filter((r) => r.priority === IssuePriority.TopPriority),
			[IssuePriority.Important]: recommendations.filter((r) => r.priority === IssuePriority.Important),
			[IssuePriority.Optional]: recommendations.filter((r) => r.priority === IssuePriority.Optional),
		}),
		[recommendations],
	);

	const priorityCounts = useMemo(
		() => ({
			high: recommendationsByPriority[IssuePriority.TopPriority].length,
			medium: recommendationsByPriority[IssuePriority.Important].length,
			low: recommendationsByPriority[IssuePriority.Optional].length,
			all: recommendations.length,
		}),
		[recommendationsByPriority, recommendations.length],
	);

	const handlePriorityChange = useCallback(
		(priority: IssuePriority | "all") => {
			setSelectedPriority(priority);
			if (priority !== "all") {
				setChosenLevel(priority);
			}
			track("recommendation_priority_changed", {
				priority,
				high_count: priorityCounts.high,
				medium_count: priorityCounts.medium,
				low_count: priorityCounts.low,
			});
		},
		[setChosenLevel, priorityCounts, track],
	);

	const handleTabChange = useCallback(
		(_: SyntheticEvent, newValue: string) => {
			setCurrentTab(newValue as TabValue);
			track("recommendation_drawer_tab_changed", {
				new_tab: newValue,
				recommendations_count: recommendationsByPriority.all.length,
			});
		},
		[recommendationsByPriority],
	);

	return (
		<Drawer
			sx={{
				width: DRAWER_WIDTH_IN_PX,
				overflow: "auto",
				color: INK.SECONDARY,
				"& .MuiDrawer-paper": {
					width: DRAWER_WIDTH_IN_PX,
					height: `calc(100vh - ${topGap}) !important`,
					top: `${topGap} !important`,
					bottom: "0 !important",
					borderTop: `1px solid ${alpha(INK_BLACK, 0.12)}`,
					position: "fixed !important",
					right: open ? 0 : `-${DRAWER_WIDTH_IN_PX}px`,
					transition: "right 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
					backgroundColor: SURFACE,
					transform: "none !important", // Prevent MUI transforms
				},
			}}
			variant="persistent"
			anchor="right"
			open={open}
		>
			<DrawerHeader>
				<Tabs
					value={currentTab}
					onChange={handleTabChange}
					sx={{
						minHeight: "36px",
						overflow: "visible", // Allow badge to show
						"& .MuiTabs-scroller": {
							overflow: "visible !important", // Override scroller overflow
						},
						"& .MuiTabs-flexContainer": {
							overflow: "visible", // Allow flexContainer overflow
						},
						"& .MuiTab-root": {
							minHeight: "36px",
							textTransform: "none",
							color: INK.SECONDARY,
							fontSize: "14px",
							padding: "4px 8px",
							minWidth: "auto",
							overflow: "visible", // Allow tab content to overflow
							"&.Mui-selected": {
								color: INK.SECONDARY,
								fontWeight: 600,
							},
						},
						"& .MuiTabs-indicator": {
							backgroundColor: INK.SECONDARY,
						},
					}}
				>
					{!isMedicalDepartment && (
						<Tab
							// Annie walkthrough anchor. Its own step, because the drawer opens on Requests (see
							// `currentTab`'s initial value) and joyride's overlay only lets clicks through to the
							// element it is spotlighting — a step that merely TOLD the user to switch tabs would be
							// asking for something the overlay blocks.
							data-annie-target="staffing-recommendations-tab"
							label="Recommendations"
							value="Recommendations"
							icon={<img height={16} width={16} src="/autoawsome.svg" alt="AutoAwesome" />}
							iconPosition="start"
						/>
					)}
					{!isMedicalDepartment && <Tab label="Requests" value={TabValue.Requests} />}
					{isMedicalDepartment && <Tab label="Requests" value={TabValue.MedicalDepartmentRequests} />}
					{isTimeOffRequestsEnabled && <Tab label="Time Off" value={TabValue.TimeOffRequests} />}
					<Tab
						// Annie walkthrough anchor, and the desktop home of staff messaging: this tab renders the whole
						// inbox (`StaffCommunication`), with no `isMobile` gate anywhere on the path. Its own step for
						// the same reason as `staffing-recommendations-tab` above — the drawer opens on Requests, and
						// joyride's overlay only lets clicks through to the element it is spotlighting.
						data-annie-target="staffing-messages-tab"
						label="Chat"
						value="Chat"
					/>
					{isAnyDemo && !isSummaryTabHiddenForDepartment(chosenDepartment.department_id) && <Tab label="Summary" value="Summary" />}
				</Tabs>
				<IconButton data-testid="recommendations-drawer-close" sx={{ p: 0, m: 0, ml: "auto" }} onClick={handleDrawerClose}>
					<CloseIcon sx={{ fontSize: 20 }} />
				</IconButton>
			</DrawerHeader>

			<Box sx={{ px: 2, py: 0, position: "relative", height: "100%" }}>
				<DrawerTabsContent
					currentTab={currentTab}
					selectedPriority={selectedPriority}
					handlePriorityChange={handlePriorityChange}
					priorityCounts={priorityCounts}
					recommendationsByPriority={recommendationsByPriority}
					commonRecommendationProps={commonRecommendationProps}
					handleOpenDialog={handleOpenDialog}
					setCurrentTab={setCurrentTab}
					date={date}
					requestId={requestId}
				/>
			</Box>
		</Drawer>
	);
};

export default memo(RecommendationsDrawer);
